<script setup lang="ts">
import type { AgentProfileHeroProps } from '.';

import { cn } from '@/utils';

import { BrokerAvatar } from '~/components/BrokerAvatar';
import { getBrokerHighlightsStyles } from '~/components/BrokerHighlights';
import { Container } from '~/components/Container';
import { GlassButton } from '~/components/GlassButton';
import { Skeleton } from '~/components/ui/skeleton';

const props = withDefaults(defineProps<AgentProfileHeroProps>(), {
	isLoading: false,
	name: '',
	profilePictureUrl: '',
	companyName: '',
	companyLogoUrl: '',
	backLabel: 'Voltar'
});

defineEmits<{
	(e: 'back-click'): void;
}>();

const hasCompany = computed<boolean>(() => !!props.companyName);
const highlightSkeletonStyles = getBrokerHighlightsStyles('dark', 'fluid');

// A foto é renderizada com `NuxtImg` em vez de pelo `BrokerAvatar`: ele passa
// pelo `AvatarImage` do reka-ui, que sonda a URL com um `new Image()` próprio e
// ignora `srcset` — o navegador baixaria um candidato e o reka outro, dois
// arquivos para o LCP da página. Aqui sai um download só, no tamanho de cada
// breakpoint. O `BrokerAvatar` segue cobrindo a ausência de foto e, via
// `@error`, a URL quebrada — que era o que a sondagem do reka detectava.
const hasImageError = ref(false);

// Zera ao trocar de corretor, senão um erro anterior prenderia o fallback.
watch(
	() => props.profilePictureUrl,
	() => {
		hasImageError.value = false;
	}
);

const showPhoto = computed<boolean>(
	() => !!props.profilePictureUrl && !hasImageError.value
);
</script>

<template>
	<!-- A borda separa o header preto do topo da hero, que tem a mesma cor de
	     fundo: sem ela as duas faixas leem como um bloco só. -->
	<section
		:class="
			cn(
				'relative w-full border-t border-primary-light-4 bg-primary xl:flex xl:min-h-[500px] xl:flex-col',
				props.class
			)
		"
	>
		<div
			v-if="!isLoading"
			aria-hidden="true"
			class="pointer-events-none absolute inset-0 z-0 overflow-hidden bg-[url(/images/agent-profile/hero-bg-mobile.svg)] bg-cover bg-center bg-no-repeat md:hidden"
		/>
		<div
			v-if="!isLoading"
			aria-hidden="true"
			class="pointer-events-none absolute inset-0 z-0 hidden overflow-hidden bg-[url(/images/agent-profile/hero-bg-tablet.svg)] bg-cover bg-center bg-no-repeat md:block lg:hidden"
		/>
		<div
			v-if="!isLoading"
			aria-hidden="true"
			class="pointer-events-none absolute inset-0 z-0 hidden overflow-hidden bg-[url(/images/agent-profile/hero-bg-desktop.svg)] bg-cover bg-center bg-no-repeat lg:block"
		/>

		<!-- z-20 e não z-10: o `Container` abaixo também é posicionado e vem
		     depois no DOM, então com o mesmo z-index a caixa dele cobriria esta
		     barra e os cliques em Voltar e Compartilhar cairiam nele. -->
		<div
			class="absolute inset-x-5 top-3 z-20 flex items-center justify-between md:inset-x-8 lg:inset-x-12 lg:top-5"
		>
			<GlassButton
				size="sm"
				class="lg:size-12"
				:aria-label="backLabel"
				@click="$emit('back-click')"
			>
				<IconMaterialSymbolsLightArrowBack class="size-5 text-white" />
			</GlassButton>

			<slot name="share" />
		</div>

		<Container
			class="relative z-10 pb-5! pt-[100px]! md:min-h-[404px] md:pb-8! xl:flex xl:min-h-0 xl:flex-1 xl:items-center xl:py-0! 2xl:px-32!"
		>
			<div
				class="grid w-full grid-cols-1 gap-x-6 gap-y-8 md:grid-cols-[auto_1fr] md:items-center md:gap-x-8 lg:items-start lg:gap-x-[44px]"
			>
				<div class="md:col-start-1 md:row-span-1 md:row-start-1 lg:row-span-2">
					<Skeleton
						v-if="isLoading"
						is-active
						variant="primary"
						class="size-[160px] rounded-none motion-reduce:animate-none lg:size-[200px] xl:size-[240px]"
					/>
					<NuxtImg
						v-else-if="showPhoto"
						:src="profilePictureUrl ?? ''"
						:alt="`${name} ${companyName}`"
						sizes="160px lg:200px xl:240px"
						format="webp"
						loading="eager"
						fetchpriority="high"
						decoding="async"
						class="size-[160px] object-cover lg:size-[200px] xl:size-[240px]"
						@error="hasImageError = true"
					/>
					<!-- O avatar genérico é um círculo desenhado dentro do arquivo,
					     com os cantos transparentes. Como aqui a moldura é
					     quadrada, o `bg-white-dark-2` preenche esses cantos com a
					     mesma cor do círculo (0 0% 95%, idêntica ao #F2F2F2 do
					     asset) e ele passa a ler como quadrado. -->
					<BrokerAvatar
						v-else
						avatar-shape="square"
						loading="eager"
						avatar-fallback-class="bg-white-dark-2 text-base text-white rounded-none!"
						:show-company-avatar="false"
						:broker-name="name"
						:company-name="companyName"
						class="size-[160px] bg-white-dark-2 lg:size-[200px] xl:size-[240px]"
					/>
				</div>

				<div
					class="flex flex-col gap-1 md:col-start-2 md:row-start-1 md:gap-2 md:self-center lg:self-start"
				>
					<template v-if="isLoading">
						<div class="flex h-12 items-center xl:h-[72px]" aria-hidden="true">
							<Skeleton
								is-active
								variant="primary"
								class="h-[29px] w-[240px] max-w-full rounded-none motion-reduce:animate-none xl:h-[43px] xl:w-[329px]"
							/>
						</div>
						<div class="flex h-6 items-center xl:h-[27px]" aria-hidden="true">
							<Skeleton
								is-active
								variant="primary"
								class="h-[17px] w-[200px] max-w-full rounded-none motion-reduce:animate-none xl:w-[286px]"
							/>
						</div>
					</template>

					<template v-else>
						<h1
							class="font-matter-sq text-[32px] font-normal leading-[1.5] tracking-[-0.02em] text-white xl:text-[48px]"
						>
							{{ name }}
						</h1>

						<p
							v-if="hasCompany"
							class="flex items-center gap-2 font-sans text-base font-normal leading-[1.5] tracking-normal text-white-dark-4 xl:text-[18px]"
						>
							<span>Corretor na</span>

							<span class="flex items-center gap-2">
								<NuxtImg
									v-if="companyLogoUrl"
									:src="companyLogoUrl"
									:alt="companyName ?? ''"
									width="20"
									height="20"
									format="webp"
									loading="lazy"
									decoding="async"
									class="size-5 shrink-0 rounded-full object-cover"
								/>

								<span class="text-white">{{ companyName }}</span>
							</span>
						</p>
					</template>
				</div>

				<div
					v-if="isLoading || $slots.stats"
					class="w-full min-w-0 md:col-start-1 md:col-end-3 md:row-start-2 lg:col-start-2"
				>
					<div
						v-if="isLoading"
						:class="highlightSkeletonStyles.list"
						aria-hidden="true"
					>
						<Skeleton
							v-for="index in 4"
							:key="index"
							is-active
							variant="primary"
							:class="[
								highlightSkeletonStyles.card,
								'rounded-none motion-reduce:animate-none'
							]"
						/>
					</div>
					<slot v-else name="stats" />
				</div>
			</div>
		</Container>
	</section>
</template>
